import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { Spinner, Toast, css, Modal, PageHeader, IconBtn, ROL_COLORS, Paginacion, Label } from '../components/UI';
import { Icono, ConIcono } from '../components/Iconos';
import { useMotivosRechazo, SelectMotivoRechazo } from '../components/MotivoRechazo';
import EditarOrdenCompra from '../components/EditarOrdenCompra';
import { useDireccionesEntrega, SelectorEntrega, entregaInicial, entregaParaApi, entregaValida } from '../components/DireccionEntrega';

// Pipeline: pendiente → aprobada (1ª aprob. jefe compras) → cotizada (comprador
// eligió ganador) → autorizada (2ª aprob.) → orden_generada (O.C. emitidas)
// → solicitada (solicitud de pago creada).
// CRÉDITO: el solicitante valida la recepción antes del pago. CONTADO: el pago
// se genera de inmediato y la recepción se valida cuando llega la mercancía.
const BASE = process.env.REACT_APP_API_URL || '/api';
const ESTADOS = ['en_validacion', 'pendiente', 'aprobada', 'cotizada', 'autorizada', 'orden_generada', 'solicitada', 'rechazada'];
const META = {
  en_validacion:  { color: 'var(--warn)', bg: 'var(--warn-bg)', line: 'var(--warn-line)', label: 'Aprobación del área' },
  pendiente:      { color: 'var(--warn)', bg: 'var(--warn-bg)', line: 'var(--warn-line)', label: 'Aprobación de Compras' },
  aprobada:       { color: 'var(--ok)', bg: 'var(--ok-bg)', line: 'var(--ok-line)', label: 'Aprobada' },
  cotizada:       { color: 'var(--ink-soft)', bg: 'var(--panel-hi)', line: 'var(--mesh-soft)', label: 'Cotizada' },
  autorizada:     { color: 'var(--info)', bg: 'var(--info-bg)', line: 'var(--info-line)', label: 'Autorizada' },
  orden_generada: { color: 'var(--st-por-pagar)', bg: 'var(--info-bg)', line: 'var(--info-line)', label: 'Orden generada' },
  solicitada:     { color: 'var(--st-pagada)', bg: 'var(--info-bg)', line: 'var(--info-line)', label: 'Solicitada' },
  rechazada:      { color: 'var(--danger)', bg: 'var(--danger-bg)', line: 'var(--danger-line)', label: 'Rechazada' },
};

// La recepción se valida solo con la orden ya validada por Finanzas (o si esa etapa no aplica)
const finanzasLiberada = (r) => !r.validacionFinanzas?.aplica || r.validacionFinanzas.estado === 'aprobada';

const Badge = ({ estado }) => {
  const m = META[estado] || { color: 'var(--glass)', bg: 'var(--panel-hi)', line: 'var(--mesh-soft)', label: estado };
  return <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11.5, fontWeight: 700, background: m.bg, color: m.color, border: `1px solid ${m.line}`, whiteSpace: 'nowrap' }}>{m.label}</span>;
};

export default function HistorialRequisiciones({ onNavigate }) {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [filtros, setFiltros] = useState({ estado: '', empresa: '', q: '', page: 1 });
  const [qInput, setQInput] = useState('');
  const [empresas, setEmpresas] = useState([]);
  // Decisión del jefe de compras: aprobar/autorizar (comentario opcional) o rechazar (motivo del catálogo + detalle)
  const [decision, setDecision] = useState(null); // { id, folio, estado, motivo, categoria }
  const motivosRechazo = useMotivosRechazo();
  const [editarOrden, setEditarOrden] = useState(null); // { req, orden }
  // Modal previo a generar la solicitud de pago: motivo de pago + concepto (partidas del ganador)
  const [generarModal, setGenerarModal] = useState({ open: false, req: null, ganador: null, motivo: '', concepto: '' });
  // Emisión de órdenes de compra (una por proveedor adjudicado) tras la autorización
  const [ordenModal, setOrdenModal] = useState({ open: false, req: null, condicionPago: 'credito', entrega: { id: '', texto: '' }, observaciones: '' });
  const direccionesEntrega = useDireccionesEntrega();
  // Validación de recepción de una orden (el solicitante dueño)
  const [recepcionModal, setRecepcionModal] = useState({ open: false, req: null, orden: null, comentario: '', archivo: null, facturas: [], recibidas: [] });
  const recepcionInputRef = useRef(null);
  const facturaInputRef = useRef(null);
  const [motivos, setMotivos] = useState([]);
  const [motivoQ, setMotivoQ] = useState(''); // búsqueda en vivo del motivo de pago
  const [comentario, setComentario] = useState('');
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [procesando, setProcesando] = useState(null);
  const comentarioRef = useRef(null);

  const toast_ = (msg, tipo = 'ok') => { setToast({ msg, tipo }); setTimeout(() => setToast(null), 4500); };
  const esJefe = ['admin', 'jefe_compras'].includes(user?.rol);
  const esComprador = ['admin', 'comprador'].includes(user?.rol);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(filtros).filter(([, v]) => v))).toString();
      setData(await api.get(`/requisiciones?${qs}`));
    } catch (e) { toast_(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filtros]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/empresas').then(setEmpresas).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => setFiltros(f => f.q === qInput ? f : { ...f, q: qInput, page: 1 }), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  const setF = (k, v) => setFiltros(f => ({ ...f, [k]: v, page: 1 }));
  const fmt = (n) => `$${(Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

  const abrirDetalle = (r) => {
    setDetalle(r);
    setComentario('');
    if (r.comentariosNuevos > 0) {
      api.post(`/requisiciones/${r._id}/comentarios/visto`, {}).catch(() => {});
      setData(d => ({ ...d, items: d.items.map(i => i._id === r._id ? { ...i, comentariosNuevos: 0 } : i) }));
    }
  };

  const cambiarEstado = async (id, estado, motivo = '', categoria) => {
    setProcesando(id);
    try {
      const r = await api.patch(`/requisiciones/${id}/estado`, { estado, motivo, categoria });
      toast_(`Requisición ${META[estado]?.label?.toLowerCase() || estado}`);
      if (detalle?._id === id) setDetalle(d => ({ ...d, ...r }));
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setProcesando(null); }
  };

  const confirmarDecision = async () => {
    const { id, estado, motivo, categoria } = decision;
    if (estado === 'rechazada' && !categoria) { toast_('Elige el motivo del rechazo', 'error'); return; }
    if (estado === 'rechazada' && !motivo.trim()) { toast_('Escribe el detalle del rechazo', 'error'); return; }
    setDecision(null);
    await cambiarEstado(id, estado, motivo, estado === 'rechazada' ? categoria : undefined);
  };
  const decidir = (r, estado) => setDecision({ id: r._id, folio: r.folio, estado, motivo: '', categoria: '' });

  // Lleva al comprador a la pantalla de Comparativos con los datos de la
  // requisición ya prellenados (empresa, sucursal, centro, concepto y vínculo).
  const crearComparativo = (r) => {
    const concepto = (r.partidas || []).map(p => `${p.cantidad} ${p.unidad || ''} ${p.descripcion}`.trim()).join('; ').slice(0, 300);
    localStorage.setItem('comparativoDesdeRequisicion', JSON.stringify({
      requisicionId: r._id,
      requisicionFolio: r.folio,
      empresa: r.empresa,
      sucursal: r.sucursal,
      centroCostos: r.centroCostos,
      direccion: r.direccion || '',
      concepto: concepto || `Requisición ${r.folio}`,
      moneda: r.moneda || 'MXN',
      // Partidas de la requisición: cada proveedor las desglosa para poner precios
      partidas: (r.partidas || []).map(p => ({
        codigo: p.codigo || '', descripcion: p.descripcion, unidad: p.unidad || '', cantidad: p.cantidad,
      })),
    }));
    if (onNavigate) onNavigate('comparativo');
    else toast_('Abre "Comparativo Cotizaciones" en el menú: el formulario ya quedó prellenado');
  };

  // Tras un rechazo de Finanzas (ver backend POST /requisiciones/:id/reabrir-compra)
  const reabrirCompra = async (r, destino) => {
    const textos = {
      ordenes: `Se anularán las órdenes de compra de ${r.folio} y volverá a "autorizada" para emitirlas de nuevo. ¿Continuar?`,
      cotizacion: `Se anularán las órdenes de compra de ${r.folio} y regresará a cotización (mismo comparativo); después pasa otra vez por la autorización del jefe de compras. ¿Continuar?`,
      revalidar: `Se enviará ${r.folio} otra vez a Finanzas sin cambiar las órdenes. Escribe un comentario para Finanzas (opcional):`,
    };
    let comentario = '';
    if (destino === 'revalidar') { const c = window.prompt(textos.revalidar, ''); if (c === null) return; comentario = c; }
    else if (!window.confirm(textos[destino])) return;
    setProcesando(r._id);
    try {
      await api.post(`/requisiciones/${r._id}/reabrir-compra`, { destino, comentario });
      toast_(destino === 'revalidar' ? `${r.folio} se envió de nuevo a Finanzas` : `${r.folio}: órdenes anuladas`);
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setProcesando(null); }
  };

  // Paso previo: el comprador elige el motivo de pago y revisa el concepto
  // (prellenado con las partidas de la cotización ganadora).
  const generarSolicitud = async (r) => {
    if (!r.comparativoId) { toast_('No tiene comparativo asociado', 'error'); return; }
    setProcesando(r._id);
    try {
      if (!motivos.length) {
        try { setMotivos(await api.get('/motivos')); } catch {}
      }
      setMotivoQ('');
      const comp = await api.get(`/comparativos/${r.comparativoId}`);
      // Adjudicación dividida: proveedores con cantidades asignadas por partida
      const dividida = (comp.cotizaciones || [])
        .map(c => ({ proveedor: c.proveedor, moneda: c.moneda, asignadas: (c.partidas || []).filter(p => (p.cantidadAsignada || 0) > 0) }))
        .filter(x => x.asignadas.length)
        .map(x => ({
          proveedor: x.proveedor,
          moneda: x.moneda,
          monto: x.asignadas.reduce((s, p) => s + (p.cantidadAsignada || 0) * (p.precioUnitario || 0), 0),
          resumen: x.asignadas.map(p => `${p.cantidadAsignada} ${p.unidad || ''} ${p.descripcion}`.replace(/\s+/g, ' ').trim()).join('; '),
        }));
      const ganador = (comp.cotizaciones || []).find(c => c.ganadora);
      if (!dividida.length && !ganador) { toast_('El comparativo no tiene ganador ni cantidades asignadas por partida', 'error'); return; }
      const concepto = dividida.length ? '' : ((ganador.partidas || [])
        .map(p => `${p.cantidad} ${p.unidad || ''} ${p.descripcion} (${fmt(p.importe)})`.replace(/\s+/g, ' ').trim())
        .join('; ') || comp.concepto || '');
      setGenerarModal({ open: true, req: r, ganador, dividida, motivo: '', concepto, nota: '' });
    } catch (e) { toast_(e.message, 'error'); }
    finally { setProcesando(null); }
  };

  const confirmarGenerar = async () => {
    const { req: r, motivo, concepto, dividida, nota } = generarModal;
    if (!motivo) { toast_('Selecciona el motivo de pago', 'error'); return; }
    setProcesando(r._id);
    try {
      const res = await api.post(`/comparativos/${r.comparativoId}/generar-solicitud`,
        dividida?.length
          ? { motivoPago: motivo, nota: (nota || '').trim() } // una solicitud por proveedor, mismo motivo
          : { motivoPago: motivo, observaciones: concepto.trim() });
      const folios = (res.solicitudes || [res.solicitud]).map(s => s.folio).join(', ');
      toast_(res.solicitudes?.length > 1
        ? `${res.solicitudes.length} solicitudes de pago generadas: ${folios}`
        : `Solicitud de pago ${folios} generada`);
      setGenerarModal({ open: false, req: null, ganador: null, dividida: null, motivo: '', concepto: '', nota: '' });
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setProcesando(null); }
  };

  const handlePDF = async (id, folio) => {
    try {
      const blob = await api.downloadPdf(`/requisiciones/${id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Requisicion_${folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Emite las órdenes de compra (una por proveedor adjudicado del comparativo)
  const confirmarOrdenes = async () => {
    const { req: r, condicionPago, entrega, observaciones } = ordenModal;
    if (!entregaValida(entrega)) { toast_('Elige la dirección de entrega', 'error'); return; }
    setProcesando(r._id);
    try {
      const doc = await api.post(`/requisiciones/${r._id}/ordenes-compra`, { condicionPago, ...entregaParaApi(entrega), observaciones });
      toast_(`${doc.ordenesCompra.length} orden(es) de compra generadas a ${condicionPago === 'credito' ? 'CRÉDITO' : 'CONTADO'}`);
      setOrdenModal({ open: false, req: null, condicionPago: 'credito', entrega: { id: '', texto: '' }, observaciones: '' });
      if (detalle?._id === r._id) setDetalle(d => ({ ...d, ...doc }));
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setProcesando(null); }
  };

  // El solicitante dueño confirma que llegó lo pedido en esta orden
  const confirmarRecepcion = async () => {
    const { req: r, orden, comentario, archivo, facturas = [], recibidas = [] } = recepcionModal;
    if (!archivo) { toast_('Adjunta el informe de recepción firmado (PDF o imagen)', 'error'); return; }
    setProcesando(r._id);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      facturas.forEach(f => fd.append('facturas', f));
      // cantidades recibidas alineadas al índice de las partidas de la orden
      fd.append('recibidas', JSON.stringify((orden.partidas || []).map((_, i) => recibidas[i])));
      fd.append('comentario', comentario || '');
      const doc = await api.upload(`/requisiciones/${r._id}/ordenes-compra/${orden._id}/recepcion`, fd);
      toast_(`Recepción de ${orden.folio} validada`);
      setRecepcionModal({ open: false, req: null, orden: null, comentario: '', archivo: null, facturas: [], recibidas: [] });
      if (detalle?._id === r._id) setDetalle(d => ({ ...d, ...doc }));
      load();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setProcesando(null); }
  };

  const handleOrdenPDF = async (reqId, oc) => {
    try {
      const blob = await api.downloadPdf(`/requisiciones/${reqId}/ordenes-compra/${oc._id}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `OrdenCompra_${oc.folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Formato de informe de recepción en blanco (prellenado con la orden) para imprimir/firmar
  const handleInformePDF = async (reqId, oc) => {
    try {
      const blob = await api.downloadPdf(`/requisiciones/${reqId}/ordenes-compra/${oc._id}/informe-recepcion/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `InformeRecepcion_${oc.folio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // Descarga el informe de recepción firmado que subió el solicitante
  const verInformeRecibido = async (reqId, oc) => {
    try {
      const r = await api.downloadFile(`/requisiciones/${reqId}/ordenes-compra/${oc._id}/recepcion/documento`);
      if (!r.ok) throw new Error('Documento no disponible');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: oc.recepcion?.documento?.nombreOriginal || `Recepcion_${oc.folio}` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  // PDF del cuadro comparativo ligado (para que el jefe de compras lo revise antes de autorizar)
  const verComparativoPDF = async (r) => {
    try {
      const blob = await api.downloadPdf(`/comparativos/${r.comparativoId}/pdf`);
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement('a'), { href: url, download: `Comparativo_${r.comparativoFolio}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast_(e.message, 'error'); }
  };

  const handleComentario = async () => {
    if (!comentario.trim() || !detalle) return;
    setEnviandoComentario(true);
    try {
      const r = await api.post(`/requisiciones/${detalle._id}/comentarios`, { texto: comentario });
      setDetalle(d => ({ ...d, comentarios: r.comentarios }));
      setComentario('');
      if (comentarioRef.current) comentarioRef.current.focus();
    } catch (e) { toast_(e.message, 'error'); }
    finally { setEnviandoComentario(false); }
  };

  // Botones de acción por rol y estado (espejo de las reglas del backend)
  const acciones = (r) => {
    const btns = [];
    const busy = procesando === r._id;
    const mk = (icono, label, variante, fn, title) => (
      <button key={label} onClick={fn} disabled={busy} title={title || label}
        style={{ ...css.btn(variante, variante !== 'secundario'), padding: '4px 10px', fontSize: 11.5, cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
        <ConIcono nombre={icono} size={13}>{label}</ConIcono>
      </button>
    );
    if (esJefe && r.estado === 'pendiente') {
      btns.push(mk('check', 'Aprobar', 'primario', () => decidir(r, 'aprobada')));
      btns.push(mk('cerrar', 'Rechazar', 'peligro', () => decidir(r, 'rechazada')));
    }
    if (esJefe && r.estado === 'cotizada') {
      btns.push(mk('grafica', 'Ver comparativo', 'secundario', () => verComparativoPDF(r), `Cuadro ${r.comparativoFolio} en PDF`));
      btns.push(mk('check', 'Autorizar compra', 'primario', () => decidir(r, 'autorizada'), 'Autoriza al ganador del comparativo'));
      btns.push(mk('cerrar', 'Rechazar', 'peligro', () => decidir(r, 'rechazada')));
    }
    if (esComprador && r.estado === 'aprobada' && !r.comparativoId) {
      btns.push(mk('calculadora', 'Crear comparativo', 'primario', () => crearComparativo(r)));
    }
    if (esComprador && r.estado === 'aprobada' && r.comparativoId) {
      btns.push(mk('subir', 'Enviar a autorización', 'primario', () => cambiarEstado(r._id, 'cotizada'), 'Requiere ganador seleccionado en el comparativo'));
    }
    if (esComprador && r.estado === 'autorizada') {
      btns.push(mk('documento', 'Generar orden de compra', 'primario', () => setOrdenModal({ open: true, req: r, condicionPago: 'credito', entrega: entregaInicial(direccionesEntrega, r.sucursal), observaciones: '' }), 'Emite una O.C. por proveedor adjudicado (elige contado o crédito)'));
    }
    const esDueño = String(r.usuario?._id || r.usuario) === String(user?._id);
    const vf = r.validacionFinanzas || {};
    const esperaFinanzas = vf.aplica && vf.estado !== 'aprobada';
    if (esComprador && r.estado === 'orden_generada' && !esperaFinanzas) {
      const pendCredito = (r.ordenesCompra || []).filter(o => o.condicionPago === 'credito' && !o.recepcion?.validada).length;
      btns.push(mk('tarjeta', 'Generar solicitud', 'primario', () => generarSolicitud(r),
        pendCredito ? `A crédito: faltan ${pendCredito} recepción(es) por validar del solicitante` : 'Genera la(s) solicitud(es) de pago'));
    }
    // Finanzas rechazó: el comprador rehace las órdenes, vuelve a cotizar o pide otra vez la validación
    if (esComprador && r.estado === 'orden_generada' && vf.estado === 'rechazada') {
      const conRecepcion = (r.ordenesCompra || []).some(o => o.recepcion?.validada);
      if (!conRecepcion) {
        btns.push(mk('documento', 'Rehacer órdenes', 'secundario', () => reabrirCompra(r, 'ordenes'), 'Anula las órdenes y vuelve a "autorizada" para emitirlas de nuevo'));
        btns.push(mk('calculadora', 'Volver a cotizar', 'secundario', () => reabrirCompra(r, 'cotizacion'), 'Anula las órdenes y regresa a cotización con el mismo comparativo'));
      }
      btns.push(mk('refrescar', 'Pedir validación de nuevo', 'secundario', () => reabrirCompra(r, 'revalidar'), 'Vuelve a mandar a Finanzas sin cambiar las órdenes'));
    }
    // Validar recepción: el dueño marca que llegó lo pedido (a crédito es requisito para el pago)
    if ((user?.rol === 'admin' || esDueño) && finanzasLiberada(r) && (r.ordenesCompra || []).some(o => !o.recepcion?.validada) && ['orden_generada', 'solicitada'].includes(r.estado)) {
      btns.push(mk('check', 'Validar recepción', 'primario', () => abrirDetalle(r), 'Confirma en el detalle qué órdenes ya llegaron'));
    }
    // El dueño (o admin) reabre el formulario completo mientras el comparativo no
    // haya arrancado: corregir una rechazada, o ajustar una que sigue en fila.
    // Con comparativo ya creado se bloquea (las cotizaciones se pidieron contra
    // estas partidas). Editar una aprobada la regresa a pendiente.
    const editable = ['en_validacion', 'pendiente', 'aprobada', 'rechazada'].includes(r.estado) && !r.comparativoId;
    if (editable && (user?.rol === 'admin' || esDueño)) {
      const rechazada = r.estado === 'rechazada';
      btns.push(mk(rechazada ? 'refrescar' : 'editar', rechazada ? 'Corregir y reenviar' : 'Editar', 'secundario', () => {
        localStorage.setItem('requisicionCorregir', JSON.stringify(r));
        if (onNavigate) onNavigate('nueva-requisicion');
        else toast_('Abre "Nueva Requisición" en el menú: el formulario quedó prellenado para corregir');
      }, rechazada ? 'Corrige el motivo del rechazo y vuelve a enviarla'
        : r.estado === 'aprobada' ? 'Al guardar vuelve a quedar pendiente de aprobación del jefe de compras'
        : 'Edita la requisición mientras el comprador no arme el comparativo'));
    }
    return btns;
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      <Toast msg={toast?.msg} tipo={toast?.tipo} onClose={() => setToast(null)} />
      <PageHeader
        title={user?.rol === 'solicitante' ? 'Mis Requisiciones' : 'Requisiciones de Compra'}
        subtitle={`${data.total} requisición${data.total !== 1 ? 'es' : ''} · flujo: aprobación del área → compras → cotizada → autorizada → orden de compra → Finanzas → solicitada`}
        actions={<button onClick={load} style={{ ...css.btnGhost, border: '1px solid var(--hair)', fontSize: 12 }}><ConIcono nombre="refrescar">Actualizar</ConIcono></button>} />

      {/* Filtros */}
      <div style={{ ...css.card, padding: '12px 18px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filtros.estado} onChange={e => setF('estado', e.target.value)} style={{ ...css.select(false), width: 160 }}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e} value={e}>{META[e].label}</option>)}
        </select>
        <select value={filtros.empresa} onChange={e => setF('empresa', e.target.value)} style={{ ...css.select(false), width: 190 }}>
          <option value="">Todas las compañías</option>
          {empresas.map(e => <option key={e._id} value={e.nombre}>{e.nombre}</option>)}
        </select>
        <input value={qInput} onChange={e => setQInput(e.target.value)} placeholder="Folio, material, departamento..." style={{ ...css.input(false), width: 240 }} />
        <button onClick={() => { setQInput(''); setFiltros({ estado: '', empresa: '', q: '', page: 1 }); }} style={{ ...css.btn('secundario', true), fontSize: 12 }}>Limpiar</button>
      </div>

      {/* Tabla */}
      <div style={{ ...css.card, padding: 0, overflow: 'hidden' }}>
        {loading ? <Spinner /> : data.items.length === 0 ? (
          <div style={{ padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ marginBottom: 10 }}><Icono nombre="carrito" size={28} style={{ color: 'var(--steel)' }} /></div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--glass)' }}>Sin requisiciones</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--panel)', borderBottom: '1px solid var(--hair)' }}>
                  {['Folio', 'Compañía', 'Departamento', 'Partidas', 'Total', 'Estado', user?.rol !== 'solicitante' && 'Solicitante', 'Fecha', 'Acciones'].filter(Boolean).map(h => (
                    <th key={h} style={{ padding: '10px 9px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map(r => (
                  <tr key={r._id} style={{ borderBottom: '1px solid var(--hair-soft)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '10px 9px', whiteSpace: 'nowrap' }}>
                      <span onClick={() => abrirDetalle(r)} style={{ fontFamily: 'var(--digit-font)', fontSize: 11.5, color: 'var(--plasma)', fontWeight: 700, cursor: 'pointer', background: 'var(--plasma-dim)', padding: '2px 8px', borderRadius: 5, border: '1px solid var(--plasma-line)' }}>{r.folio}</span>
                      {r.comentariosNuevos > 0 && (
                        <span onClick={() => abrirDetalle(r)} title={`${r.comentariosNuevos} comentario(s) nuevo(s)`}
                          style={{ marginLeft: 6, padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 800, cursor: 'pointer', background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger-line)', whiteSpace: 'nowrap' }}>
                          <ConIcono nombre="comentario" size={12} gap={4}>{r.comentariosNuevos}</ConIcono>
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 9px', fontWeight: 600, color: 'var(--ink)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.empresa}</td>
                    <td style={{ padding: '10px 9px', color: 'var(--glass)', fontSize: 12 }}>{r.departamento || '—'}</td>
                    <td style={{ padding: '10px 9px', color: 'var(--ink-soft)', fontSize: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={(r.partidas || []).map(p => `${p.cantidad} ${p.descripcion}`).join(', ')}>
                      {(r.partidas || []).length} partida{(r.partidas || []).length !== 1 ? 's' : ''} · {(r.partidas?.[0]?.descripcion || '').slice(0, 30)}{r.partidas?.length > 1 ? '…' : ''}
                    </td>
                    <td style={{ padding: '10px 9px', fontFamily: 'var(--digit-font)', fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmt(r.total)}</td>
                    <td style={{ padding: '10px 9px' }}>
                      <div><Badge estado={r.estado} />
                        <EstadoFinanzas r={r} />
                        {r.estado === 'rechazada' && r.motivoRechazo && (
                          <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 2, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={[r.motivoRechazoCategoria, r.motivoRechazo].filter(Boolean).join(': ')}>{r.motivoRechazoCategoria || r.motivoRechazo}</div>
                        )}
                        {r.solicitudFolio && <div style={{ fontSize: 11.5, color: 'var(--ok)', marginTop: 2 }}>→ {r.solicitudFolio}</div>}
                      </div>
                    </td>
                    {user?.rol !== 'solicitante' && (
                      <td style={{ padding: '10px 9px', fontSize: 12, color: 'var(--glass)' }}>{r.usuario?.nombre || '—'}</td>
                    )}
                    <td style={{ padding: '10px 9px', fontSize: 12, color: 'var(--glass)', whiteSpace: 'nowrap' }}>{new Date(r.createdAt).toLocaleDateString('es-MX')}</td>
                    <td style={{ padding: '10px 9px', minWidth: 210 }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', minWidth: 196 }}>
                        <IconBtn onClick={() => handlePDF(r._id, r.folio)} title="Descargar PDF" icon={<Icono nombre="descarga" size={15} />} />
                        <IconBtn onClick={() => abrirDetalle(r)} title="Ver detalle" icon={<Icono nombre="ojo" size={15} />} />
                        {acciones(r)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Paginacion page={filtros.page} pages={data.pages} onPage={p => setFiltros(f => ({ ...f, page: p }))} />
      </div>

      {editarOrden && (
        <EditarOrdenCompra req={editarOrden.req} orden={editarOrden.orden} avisar={toast_} onClose={() => setEditarOrden(null)}
          onGuardada={(doc) => { setEditarOrden(null); if (detalle?._id === doc._id) setDetalle(d => ({ ...d, ...doc, usuario: d.usuario })); load(); }} />
      )}

      {/* Modal de decisión del jefe de compras: aprobar / autorizar / rechazar */}
      <Modal open={!!decision} onClose={() => setDecision(null)} width={460}
        title={decision?.estado === 'rechazada' ? 'Rechazar requisición' : decision?.estado === 'autorizada' ? 'Autorizar compra' : 'Aprobar requisición'}>
        {decision && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--glass)' }}>
              <span style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma)' }}>{decision.folio}</span>
              {decision.estado === 'rechazada' ? ' · El solicitante verá el motivo y el detalle.' : ' · El comentario queda en el historial y en el reporte de compras.'}
            </div>
            {decision.estado === 'rechazada' && <SelectMotivoRechazo value={decision.categoria} motivos={motivosRechazo} onChange={v => setDecision(d => ({ ...d, categoria: v }))} />}
            <div>
              <Label required={decision.estado === 'rechazada'}>{decision.estado === 'rechazada' ? 'Detalle del rechazo' : 'Comentario (opcional)'}</Label>
              <textarea autoFocus value={decision.motivo} onChange={e => setDecision(d => ({ ...d, motivo: e.target.value }))} rows={3}
                style={{ ...css.input(decision.estado === 'rechazada' && !decision.motivo.trim()), resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setDecision(null)} style={{ ...css.btn('secundario', true), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarDecision} style={{ ...css.btn(decision.estado === 'rechazada' ? 'peligro' : 'primario'), fontSize: 13 }}>
                {decision.estado === 'rechazada' ? 'Confirmar rechazo' : decision.estado === 'autorizada' ? 'Autorizar' : 'Aprobar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal generar órdenes de compra: condición de pago + entrega */}
      <Modal open={ordenModal.open} onClose={() => setOrdenModal(m => ({ ...m, open: false }))} title="Generar Órdenes de Compra" width={520}>
        {ordenModal.req && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--glass)' }}>
              Se emitirá <b style={{ color: 'var(--ink)' }}>una orden por cada proveedor adjudicado</b> del comparativo {ordenModal.req.comparativoFolio}, con IVA 16%.
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 6 }}>Condición de pago</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['credito', 'Crédito', 'El solicitante valida la recepción ANTES de generar el pago'], ['contado', 'Contado', 'El pago se genera de inmediato; la recepción se valida al llegar']].map(([v, label, desc]) => (
                  <button key={v} onClick={() => setOrdenModal(m => ({ ...m, condicionPago: v }))} title={desc}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                      fontSize: 13, fontWeight: 700, transition: 'all 0.15s',
                      background: ordenModal.condicionPago === v ? 'var(--plasma-dim)' : 'var(--hair-soft)',
                      color: ordenModal.condicionPago === v ? 'var(--plasma)' : 'var(--glass)',
                      border: `1.5px solid ${ordenModal.condicionPago === v ? 'var(--plasma-line)' : 'var(--hair)'}`,
                    }}><ConIcono nombre={v === 'credito' ? 'banco' : 'billete'} size={14}>{label}</ConIcono></button>
                ))}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 5 }}>
                {ordenModal.condicionPago === 'credito'
                  ? 'A crédito: el solicitante deberá validar que llegó la mercancía antes de poder generar la solicitud de pago.'
                  : 'A contado: la solicitud de pago se puede generar de inmediato; la recepción se valida cuando llegue la mercancía.'}
              </div>
            </div>
            <SelectorEntrega value={ordenModal.entrega} onChange={v => setOrdenModal(m => ({ ...m, entrega: v }))} direcciones={direccionesEntrega} sucursal={ordenModal.req.sucursal} />
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 5 }}>Observaciones (opcional)</div>
              <textarea value={ordenModal.observaciones} onChange={e => setOrdenModal(m => ({ ...m, observaciones: e.target.value }))}
                rows={2} style={{ ...css.input(false), resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setOrdenModal(m => ({ ...m, open: false }))} style={css.btnGhost}>Cancelar</button>
              <button onClick={confirmarOrdenes} disabled={procesando === ordenModal.req._id}
                style={{ ...css.btn('primario'), opacity: procesando === ordenModal.req._id ? 0.6 : 1 }}>
                <ConIcono nombre="documento">Emitir órdenes de compra</ConIcono>
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal validar recepción de una orden */}
      <Modal open={recepcionModal.open} onClose={() => setRecepcionModal(m => ({ ...m, open: false }))} title="Validar recepción de mercancía" width={640}>
        {recepcionModal.orden && (() => {
          const parts = recepcionModal.orden.partidas || [];
          const recibidas = recepcionModal.recibidas || [];
          const setRecibida = (i, v) => setRecepcionModal(m => {
            const arr = [...(m.recibidas || [])]; arr[i] = v; return { ...m, recibidas: arr };
          });
          const subtotal = parts.reduce((s, p, i) => s + (parseFloat(recibidas[i]) || 0) * (p.precioUnitario || 0), 0);
          // Previo con la tasa de cada partida. Es sólo un estimado: al guardar, el
          // backend prefiere el IVA del XML de la factura cuando su base cuadra.
          const ivaPrev = parts.reduce((s, p, i) => {
            const tasa = Number.isFinite(Number(p.tasaIva)) ? Number(p.tasaIva) : 16;
            return s + (parseFloat(recibidas[i]) || 0) * (p.precioUnitario || 0) * (tasa / 100);
          }, 0);
          return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--ink)' }}>
              Orden <b style={{ fontFamily: 'var(--digit-font)', color: 'var(--plasma)' }}>{recepcionModal.orden.folio}</b> · {recepcionModal.orden.proveedor}
            </div>
            <div style={{ fontSize: 12, color: 'var(--glass)' }}>
              Confirma cuántas piezas recibiste de cada partida (ajústalas si llegó incompleto o de más). {recepcionModal.orden.condicionPago === 'credito' && 'La solicitud de pago se generará por el monto realmente recibido + IVA.'}
            </div>

            {/* Editor de cantidades recibidas */}
            <div style={{ border: '1px solid var(--hair)', borderRadius: 10, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--hair-soft)' }}>
                    {['Descripción', 'Pedida', 'Recibida', 'P. Unit.', 'Importe'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Descripción' ? 'left' : 'right', fontSize: 12, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parts.map((p, i) => {
                    const rec = parseFloat(recibidas[i]) || 0;
                    const dif = rec - (p.cantidad || 0);
                    return (
                      <tr key={i} style={{ borderTop: '1px solid var(--hair-soft)' }}>
                        <td style={{ padding: '5px 8px' }}>{p.descripcion}{p.codigo ? <span style={{ color: 'var(--glass)' }}> · {p.codigo}</span> : ''}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--glass)', fontFamily: 'var(--digit-font)' }}>{p.cantidad} {p.unidad || ''}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                          <input type="number" min="0" step="any" value={recibidas[i] ?? ''} onChange={e => setRecibida(i, e.target.value)}
                            style={{ ...css.input(false), width: 74, padding: '4px 6px', textAlign: 'right', fontFamily: 'var(--digit-font)',
                              borderColor: dif !== 0 ? 'var(--warn)' : undefined }} />
                          {dif !== 0 && <div style={{ fontSize: 11.5, color: 'var(--st-pendiente)', fontWeight: 700 }}>{dif > 0 ? `+${dif}` : dif}</div>}
                        </td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'var(--digit-font)', color: 'var(--glass)' }}>{fmt(p.precioUnitario)}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'var(--digit-font)', fontWeight: 600 }}>{fmt(rec * (p.precioUnitario || 0))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '8px 10px', background: 'var(--hair-soft)', borderTop: '1px solid var(--hair)', textAlign: 'right', fontSize: 12, fontFamily: 'var(--digit-font)' }}>
                <span style={{ color: 'var(--glass)', marginRight: 14 }}>Subtotal {fmt(subtotal)}</span>
                <span style={{ color: 'var(--glass)', marginRight: 14 }}>IVA {fmt(ivaPrev)}</span>
                <b style={{ color: 'var(--ok)' }}>Total {fmt(subtotal + ivaPrev)}</b>
              </div>
            </div>
            {/* Formato en blanco para imprimir, firmar y volver a subir */}
            <button onClick={() => handleInformePDF(recepcionModal.req._id, recepcionModal.orden)}
              style={{ ...css.btnGhost, alignSelf: 'flex-start', fontSize: 12 }}>
              <ConIcono nombre="descarga">Descargar formato de informe de recepción</ConIcono>
            </button>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 5 }}>
                Informe de recepción firmado <span style={{ color: 'var(--danger)' }}>*</span>
              </div>
              <input ref={recepcionInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" style={{ display: 'none' }}
                onChange={e => setRecepcionModal(m => ({ ...m, archivo: e.target.files?.[0] || null }))} />
              <button onClick={() => recepcionInputRef.current?.click()}
                style={{ ...css.btnGhost, width: '100%', textAlign: 'left', borderStyle: recepcionModal.archivo ? 'solid' : 'dashed' }}>
                <ConIcono nombre="clip">{recepcionModal.archivo ? recepcionModal.archivo.name : 'Adjuntar documento (PDF o imagen)'}</ConIcono>
              </button>
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>Obligatorio: sube el informe de recepción firmado por quien recibió.</div>
            </div>
            {/* Factura del proveedor (PDF y XML): se anexan a la solicitud de pago */}
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 5 }}>
                Factura del proveedor (PDF y XML)
              </div>
              <input ref={facturaInputRef} type="file" accept=".pdf,.xml" multiple style={{ display: 'none' }}
                onChange={e => setRecepcionModal(m => ({ ...m, facturas: [...(m.facturas || []), ...Array.from(e.target.files || [])] }))} />
              <button onClick={() => facturaInputRef.current?.click()}
                style={{ ...css.btnGhost, width: '100%', textAlign: 'left', borderStyle: (recepcionModal.facturas?.length) ? 'solid' : 'dashed' }}>
                <ConIcono nombre="clip">Adjuntar factura (PDF / XML)</ConIcono>
              </button>
              {(recepcionModal.facturas || []).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {recepcionModal.facturas.map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11.5, color: 'var(--ink)', background: 'var(--hair-soft)', borderRadius: 6, padding: '3px 8px' }}>
                      <ConIcono nombre="documento" size={13}>{f.name}</ConIcono>
                      <span onClick={() => setRecepcionModal(m => ({ ...m, facturas: m.facturas.filter((_, j) => j !== i) }))}
                        style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 800, paddingLeft: 8 }}>×</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 4 }}>Se copian como comprobantes de la solicitud de pago que se genere.</div>
            </div>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 5 }}>Comentario (opcional)</div>
              <textarea value={recepcionModal.comentario} onChange={e => setRecepcionModal(m => ({ ...m, comentario: e.target.value }))}
                rows={2} placeholder="Ej. Llegó completo el 17/07; una tarima con detalle menor" style={{ ...css.input(false), resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setRecepcionModal(m => ({ ...m, open: false }))} style={css.btnGhost}>Cancelar</button>
              <button onClick={confirmarRecepcion} disabled={procesando === recepcionModal.req?._id || !recepcionModal.archivo}
                style={{ ...css.btn('primario'), opacity: (procesando === recepcionModal.req?._id || !recepcionModal.archivo) ? 0.6 : 1 }}>
                <ConIcono nombre="check">Recibido conforme</ConIcono>
              </button>
            </div>
          </div>
          );
        })()}
      </Modal>

      {/* Modal generar solicitud de pago: motivo + concepto con partidas del ganador */}
      <Modal open={generarModal.open} onClose={() => setGenerarModal(m => ({ ...m, open: false }))}
        title={generarModal.dividida?.length ? 'Generar Solicitudes de Pago (adjudicación dividida)' : 'Generar Solicitud de Pago'} width={560}>
        {(generarModal.ganador || generarModal.dividida?.length > 0) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13 }}>
            {generarModal.dividida?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11.5, color: 'var(--ink-soft)', textTransform: 'uppercase', fontWeight: 800 }}>
                  Compra repartida entre {generarModal.dividida.length} proveedores · {generarModal.req?.comparativoFolio}
                </div>
                {generarModal.dividida.map((d, i) => (
                  <div key={i} style={{ padding: '9px 14px', borderRadius: 10, background: 'var(--panel-hi)', border: '1px solid var(--mesh-soft)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontWeight: 700, color: 'var(--ink)' }}>{d.proveedor}</span>
                      <span style={{ fontFamily: 'var(--digit-font)', fontWeight: 800, color: 'var(--plasma)', whiteSpace: 'nowrap' }}>{fmt(d.monto)} {d.moneda || 'MXN'}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 3 }}>{d.resumen}</div>
                  </div>
                ))}
                <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--glass)' }}>
                  Total: <b style={{ fontFamily: 'var(--digit-font)', color: 'var(--ok)' }}>{fmt(generarModal.dividida.reduce((s, d) => s + d.monto, 0))}</b>
                  {' '}· se creará <b>una solicitud de pago por proveedor</b> con el mismo motivo
                </div>
              </div>
            ) : (
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--plasma-dim)', border: '1px solid var(--plasma-line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11.5, color: 'var(--plasma)', textTransform: 'uppercase', fontWeight: 700 }}>Proveedor ganador · {generarModal.req?.comparativoFolio}</div>
                <div style={{ fontWeight: 700, color: 'var(--ink)', marginTop: 2 }}><ConIcono nombre="estrella" size={14}>{generarModal.ganador?.proveedor}</ConIcono></div>
              </div>
              <div style={{ fontFamily: 'var(--digit-font)', fontWeight: 800, fontSize: 15, color: 'var(--plasma)', whiteSpace: 'nowrap' }}>
                {fmt(generarModal.ganador?.monto)} {generarModal.ganador?.moneda || 'MXN'}
              </div>
            </div>
            )}
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 4 }}>Motivo de pago <span style={{ color: 'var(--danger)' }}>*</span></div>
              {generarModal.motivo ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'var(--ok-bg)', color: 'var(--ok)', border: '1px solid var(--ok-line)' }}>
                    <ConIcono nombre="tarjeta" size={14}>{generarModal.motivo}</ConIcono>
                  </span>
                  <button onClick={() => { setGenerarModal(m => ({ ...m, motivo: '' })); setMotivoQ(''); }} title="Cambiar motivo"
                    style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid var(--hair)', background: 'var(--panel)', color: 'var(--glass)', cursor: 'pointer', fontSize: 13 }}>×</button>
                </div>
              ) : (
                <div>
                  <input autoFocus value={motivoQ} onChange={e => setMotivoQ(e.target.value)}
                    placeholder="Escribe para buscar el motivo de pago..."
                    style={css.input(true)} />
                  <div style={{ marginTop: 4, border: '1px solid var(--hair)', borderRadius: 10, maxHeight: 170, overflowY: 'auto', background: 'var(--panel)' }}>
                    {(() => {
                      const t = motivoQ.trim().toLowerCase();
                      const lista = (t ? motivos.filter(mo => mo.nombre.toLowerCase().includes(t)) : motivos).slice(0, 40);
                      if (!motivos.length) return <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--glass)' }}>Cargando motivos…</div>;
                      if (!lista.length) return <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--glass)' }}>Sin coincidencias para "{motivoQ}".</div>;
                      return lista.map(mo => (
                        <div key={mo._id} onClick={() => setGenerarModal(m => ({ ...m, motivo: mo.nombre }))}
                          style={{ padding: '7px 12px', fontSize: 12.5, color: 'var(--ink)', cursor: 'pointer', borderBottom: '1px solid var(--hair-soft)' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          {mo.nombre}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
            {generarModal.dividida?.length > 0 ? (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 4 }}>Nota adicional (opcional)</div>
                <input value={generarModal.nota} onChange={e => setGenerarModal(m => ({ ...m, nota: e.target.value }))}
                  placeholder="Se agregará al concepto de todas las solicitudes..." style={{ ...css.input(false), fontSize: 12.5 }} />
                <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 3 }}>El concepto de cada solicitud se arma automáticamente con las partidas asignadas a su proveedor.</div>
              </div>
            ) : (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 4 }}>Concepto del pago (partidas de la cotización)</div>
              <textarea value={generarModal.concepto} onChange={e => setGenerarModal(m => ({ ...m, concepto: e.target.value }))}
                rows={4} style={{ ...css.input(false), resize: 'vertical', fontSize: 12.5 }} />
              <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 3 }}>Prellenado con las partidas del ganador; puedes ajustarlo antes de generar.</div>
            </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setGenerarModal(m => ({ ...m, open: false }))} style={{ ...css.btn('secundario', true), fontSize: 13 }}>Cancelar</button>
              <button onClick={confirmarGenerar} disabled={procesando === generarModal.req?._id}
                style={{ ...css.btn('primario'), fontSize: 13, opacity: procesando === generarModal.req?._id ? 0.6 : 1 }}>
                {procesando === generarModal.req?._id ? 'Generando…'
                  : <ConIcono nombre="tarjeta">{generarModal.dividida?.length > 0 ? `Generar ${generarModal.dividida.length} solicitudes de pago` : 'Generar solicitud de pago'}</ConIcono>}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal detalle */}
      {/* Base: los modales de acción (rechazo, orden, recepción, generar) se apilan encima */}
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title={`Requisición ${detalle?.folio}`} width={680} zIndex={900}>
        {detalle && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
              {[
                ['Compañía', detalle.empresa], ['Sucursal', detalle.sucursal],
                ['Centro de Costos', detalle.centroCostos], ['Departamento', detalle.departamento],
                ['Solicitante', detalle.usuario?.nombre], ['Fecha', detalle.fechaSolicitud],
                ['Proveedor sugerido', detalle.proveedorSugerido?.nombre],
                ['Comparativo', detalle.comparativoFolio], ['Solicitud de pago', detalle.solicitudFolio],
              ].filter(([, v]) => v).map(([l, v]) => (
                <div key={l} style={{ padding: '5px 0', borderBottom: '1px solid var(--hair-soft)' }}>
                  <div style={{ fontSize: 11.5, color: 'var(--glass)', textTransform: 'uppercase' }}>{l}</div>
                  <div style={{ color: 'var(--ink)', fontWeight: 500 }}>{v}</div>
                </div>
              ))}
            </div>
            {detalle.motivoRechazo && (
              <div style={{ padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid var(--danger-line)', color: 'var(--danger)', fontSize: 12.5 }}>
                <b>Motivo de rechazo:</b> {detalle.motivoRechazoCategoria ? `${detalle.motivoRechazoCategoria} — ` : ''}{detalle.motivoRechazo}
              </div>
            )}

            {/* Partidas */}
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 6 }}>Partidas</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: 'var(--panel)' }}>
                  {['Cant.', 'Código', 'Descripción', 'P. Unit.', 'Subtotal'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontSize: 12, color: 'var(--glass)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {(detalle.partidas || []).map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--hair-soft)' }}>
                      <td style={{ padding: '5px 8px' }}>{p.cantidad} {p.unidad}</td>
                      <td style={{ padding: '5px 8px', fontFamily: 'var(--digit-font)', fontSize: 11.5 }}>{p.codigo || '—'}</td>
                      <td style={{ padding: '5px 8px' }}>{p.descripcion}</td>
                      <td style={{ padding: '5px 8px', fontFamily: 'var(--digit-font)' }}>{fmt(p.precioUnitario)}</td>
                      <td style={{ padding: '5px 8px', fontFamily: 'var(--digit-font)', fontWeight: 600 }}>{fmt(p.importe)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ textAlign: 'right', marginTop: 8, fontSize: 12.5 }}>
                <span style={{ color: 'var(--glass)', marginRight: 14 }}>Subtotal {fmt(detalle.subtotal)}</span>
                <span style={{ color: 'var(--glass)', marginRight: 14 }}>IVA {fmt(detalle.iva)}</span>
                <span style={{ fontWeight: 800, color: 'var(--plasma)' }}>TOTAL {fmt(detalle.total)} {detalle.moneda}</span>
              </div>
            </div>

            {(detalle.justificacion || detalle.justificacionImagenes?.length > 0) && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 4 }}>Justificación</div>
                {detalle.justificacion && <div style={{ color: 'var(--ink-soft)' }}>{detalle.justificacion}</div>}
                {/* Evidencia que adjuntó el solicitante: clic para verla completa */}
                {detalle.justificacionImagenes?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
                    {detalle.justificacionImagenes.map(img => {
                      const url = `${BASE}/requisiciones/${detalle._id}/justificacion-imagenes/${img._id}/preview?token=${localStorage.getItem('token')}`;
                      return (
                        <div key={img._id} style={{ width: 118 }}>
                          <img src={url} alt={img.nombreOriginal} title={`${img.nombreOriginal} — clic para verla completa`}
                            onClick={() => window.open(url, '_blank')}
                            style={{ width: 118, height: 88, objectFit: 'cover', borderRadius: 8, border: '1.5px solid var(--hair)', background: 'var(--panel)', display: 'block', cursor: 'zoom-in' }} />
                          <div title={img.nombreOriginal} style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{img.nombreOriginal}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Órdenes de compra: PDF por orden y validación de recepción */}
            {detalle.ordenesCompra?.length > 0 && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 8 }}>
                  Órdenes de Compra ({detalle.ordenesCompra.length})
                </div>
                {detalle.ordenesCompra.map((oc) => {
                  const esDueñoDetalle = user?.rol === 'admin' || String(detalle.usuario?._id || detalle.usuario) === String(user?._id);
                  const puedeValidar = esDueñoDetalle && !oc.recepcion?.validada && finanzasLiberada(detalle);
                  return (
                    <div key={oc._id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', marginBottom: 6, borderRadius: 8, background: 'var(--hair-soft)', border: `1px solid ${oc.recepcion?.validada ? 'var(--ok-line)' : 'var(--hair)'}` }}>
                      <span style={{ fontFamily: 'var(--digit-font)', fontSize: 11.5, fontWeight: 700, color: 'var(--plasma)' }}>{oc.folio}</span>
                      <span style={{ fontSize: 12.5, color: 'var(--ink)', fontWeight: 600 }}>{oc.proveedor}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11.5, fontWeight: 800, background: oc.condicionPago === 'credito' ? 'var(--info-bg)' : 'var(--warn-bg)', color: oc.condicionPago === 'credito' ? 'var(--info)' : 'var(--warn)', border: `1px solid ${oc.condicionPago === 'credito' ? 'var(--info-line)' : 'var(--warn-line)'}` }}>
                        <ConIcono nombre={oc.condicionPago === 'credito' ? 'banco' : 'billete'} size={12} gap={4}>{oc.condicionPago === 'credito' ? 'CRÉDITO' : 'CONTADO'}</ConIcono>
                      </span>
                      <span style={{ fontFamily: 'var(--digit-font)', fontSize: 12, color: 'var(--ink-soft)' }}>{fmt(oc.total)}</span>
                      {(oc.entregaNombre || oc.direccionEntrega) && <span title={[oc.direccionEntrega, oc.entregaHorario].filter(Boolean).join(' · ')} style={{ fontSize: 11.5, color: 'var(--glass)' }}><ConIcono nombre="pin" size={12} gap={4}>{oc.entregaNombre || oc.direccionEntrega}{oc.entregaFueraCatalogo ? ' (fuera de catálogo)' : ''}</ConIcono></span>}
                      {(oc.revision || 1) > 1 && <span title="La orden se editó después de emitirse" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--plasma)' }}>Rev. {oc.revision}</span>}
                      {oc.solicitudFolio && <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>→ {oc.solicitudFolio}</span>}
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                        {oc.recepcion?.validada ? (
                          <span title={`${oc.recepcion.nombreUsuario} · ${new Date(oc.recepcion.fecha).toLocaleDateString('es-MX')}${oc.recepcion.comentario ? ` — ${oc.recepcion.comentario}` : ''}`}
                            style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ok)', cursor: 'help' }}><ConIcono nombre="check" size={13}>Recibida</ConIcono></span>
                        ) : !finanzasLiberada(detalle) ? (
                          <span title="La recepción se valida cuando Finanzas aprueba la orden" style={{ fontSize: 11.5, fontWeight: 700, color: detalle.validacionFinanzas?.estado === 'rechazada' ? 'var(--danger)' : 'var(--warn)' }}>
                            {detalle.validacionFinanzas?.estado === 'rechazada' ? <ConIcono nombre="cerrar" size={13}>Rechazada por Finanzas: Compras la corrige</ConIcono> : <ConIcono nombre="reloj" size={13}>Espera validación de Finanzas</ConIcono>}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--warn)' }}><ConIcono nombre="reloj" size={13}>Por recibir</ConIcono></span>
                        )}
                        {puedeValidar && (
                          <button onClick={() => setRecepcionModal({ open: true, req: detalle, orden: oc, comentario: '', archivo: null, facturas: [], recibidas: (oc.partidas || []).map(p => String(p.cantidad ?? '')) })}
                            style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--ok-line)', background: 'var(--ok-bg)', color: 'var(--ok)' }}>
                            <ConIcono nombre="check" size={13}>Validar recepción</ConIcono>
                          </button>
                        )}
                        {oc.recepcion?.documento?.nombre && (
                          <button onClick={() => verInformeRecibido(detalle._id, oc)} title="Ver el informe de recepción firmado"
                            style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--ok-line)', background: 'var(--ok-bg)', color: 'var(--ok)' }}>
                            <ConIcono nombre="documento" size={13}>Informe</ConIcono>
                          </button>
                        )}
                        {esComprador && detalle.estado === 'orden_generada' && !oc.solicitudPago && (
                          <button onClick={() => setEditarOrden({ req: detalle, orden: oc })} title="Corregir la orden (cantidades, precios, condición de pago, entrega)"
                            style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--ink-soft)' }}>
                            <ConIcono nombre="editar" size={13}>Editar</ConIcono>
                          </button>
                        )}
                        <button onClick={() => handleOrdenPDF(detalle._id, oc)} title="Descargar PDF de la orden"
                          style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--hair)', background: 'var(--hair-soft)', color: 'var(--ink-soft)' }}>
                          <ConIcono nombre="descarga" size={13}>PDF</ConIcono>
                        </button>
                      </span>
                      {oc.revisiones?.length > 0 && (
                        <details style={{ flexBasis: '100%', fontSize: 11.5, color: 'var(--ink-soft)' }}>
                          <summary style={{ cursor: 'pointer', color: 'var(--glass)' }}>{oc.revisiones.length} {oc.revisiones.length === 1 ? 'cambio' : 'cambios'} después de emitirse</summary>
                          {[...oc.revisiones].reverse().map((rv, i) => (
                            <div key={i} style={{ padding: '6px 0 2px', borderTop: '1px solid var(--hair)', marginTop: 4 }}>
                              <b>Rev. {rv.revision}</b> · {rv.nombreUsuario} · {new Date(rv.fecha).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}
                              {Math.abs((rv.totalNuevo || 0) - (rv.totalAnterior || 0)) > 0.005 && <> · {fmt(rv.totalAnterior)} → {fmt(rv.totalNuevo)}</>}
                              <div style={{ fontStyle: 'italic' }}>"{rv.motivo}"</div>
                              <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>{(rv.cambios || []).map((c, j) => <li key={j}>{c}</li>)}</ul>
                            </div>
                          ))}
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Aprobaciones (área y Finanzas) */}
            <CadenaAprobacion r={detalle} />

            {/* Historial de estados */}
            {detalle.historialEstados?.length > 0 && (
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 8 }}>Historial</div>
                {detalle.historialEstados.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, marginBottom: 4 }}>
                    <Badge estado={h.estado} />
                    <span style={{ color: 'var(--glass)' }}>{h.nombreUsuario}</span>
                    {h.motivo && <span style={{ color: 'var(--glass)', fontStyle: 'italic' }}>"{h.motivo}"</span>}
                    <span style={{ color: 'var(--glass)', marginLeft: 'auto', fontSize: 11.5 }}>{new Date(h.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Comentarios */}
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 8 }}>
                Comentarios {detalle.comentarios?.length > 0 && `(${detalle.comentarios.length})`}
              </div>
              {detalle.comentarios?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 160, overflowY: 'auto' }}>
                  {detalle.comentarios.map((c, i) => (
                    <div key={i} style={{ background: 'var(--hair-soft)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--hair)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: ROL_COLORS[c.rol] || 'var(--ink)' }}>{c.nombre} <span style={{ fontWeight: 400, color: 'var(--glass)', fontSize: 11.5 }}>({c.rol})</span></span>
                        <span style={{ fontSize: 11.5, color: 'var(--glass)' }}>{new Date(c.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{c.texto}</div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ fontSize: 12, color: 'var(--glass)', marginBottom: 8 }}>Sin comentarios aún.</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <input ref={comentarioRef} value={comentario} onChange={e => setComentario(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComentario(); } }}
                  placeholder="Escribe un comentario... (Enter para enviar)" style={{ ...css.input(false), flex: 1, fontSize: 12.5 }} />
                <button onClick={handleComentario} disabled={!comentario.trim() || enviandoComentario}
                  style={{ ...css.btn('secundario'), fontSize: 12, padding: '0 16px' }}>{enviandoComentario ? '...' : 'Enviar'}</button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => handlePDF(detalle._id, detalle.folio)} style={{ ...css.btn('secundario'), flex: 1 }}><ConIcono nombre="descarga">Descargar PDF</ConIcono></button>
              {acciones(detalle)}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Estado de la validación de Finanzas, bajo el badge de la requisición
function EstadoFinanzas({ r }) {
  const v = r.validacionFinanzas || {};
  if (!v.aplica || r.estado !== 'orden_generada') return null;
  const t = { sin_iniciar: null, pendiente: ['Esperando Finanzas', 'var(--warn)'], aprobada: ['Validada por Finanzas', 'var(--ok)'], rechazada: ['Rechazada por Finanzas', 'var(--danger)'] }[v.estado];
  if (!t) return null;
  return <div style={{ fontSize: 11.5, fontWeight: 700, color: t[1], marginTop: 3 }} title={v.comentario || ''}>{t[0]}</div>;
}

// Cadena de aprobación en el detalle: quién aprobó cada paso, cuándo y qué dijo
function CadenaAprobacion({ r }) {
  const a = r.aprobacionArea || {};
  const v = r.validacionFinanzas || {};
  if (!a.aplica && !v.aplica) return null;
  const f = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
  const color = { pendiente: 'var(--st-pendiente)', aprobado: 'var(--ok)', aprobada: 'var(--ok)', rechazado: 'var(--danger)', rechazada: 'var(--danger)', sin_iniciar: 'var(--glass)' };
  const texto = { pendiente: 'Pendiente', aprobado: 'Aprobó', aprobada: 'Validó', rechazado: 'Rechazó', rechazada: 'Rechazó', sin_iniciar: 'Aún no le toca' };
  const fila = (titulo, quien, estado, resolvio, fecha, comentario, key) => (
    <div key={key} style={{ display: 'flex', gap: 10, alignItems: 'baseline', fontSize: 12.5, padding: '5px 0', borderTop: '1px solid var(--mesh-soft)', flexWrap: 'wrap' }}>
      <span style={{ minWidth: 150, fontWeight: 600 }}>{titulo}</span>
      <span style={{ color: 'var(--ink-soft)' }}>{quien}</span>
      <span style={{ color: color[estado], fontWeight: 700 }}>{texto[estado]}{resolvio ? ` · ${resolvio}` : ''}</span>
      {fecha && <span style={{ color: 'var(--glass)', fontSize: 11.5 }}>{f(fecha)}</span>}
      {comentario && <span style={{ color: 'var(--glass)', fontStyle: 'italic', flexBasis: '100%', paddingLeft: 160 }}>"{comentario}"</span>}
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', marginBottom: 4 }}>Aprobaciones</div>
      {a.aplica && (a.pasos || []).map((p, i) => fila(
        a.pasos.length > 1 ? `Área · paso ${i + 1}` : 'Área',
        (p.aprobadores || []).map(x => x.nombre).join(' o '), p.estado, p.nombreResolvio, p.fecha, p.comentario, `a${i}`))}
      {a.aplica && a.regla?.descripcion && <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 2 }}>Regla: {a.regla.descripcion}</div>}
      {v.aplica && fila('Finanzas', v.estado === 'sin_iniciar' ? 'Al emitirse las órdenes de compra' : '', v.estado, v.nombreResolvio, v.fecha, v.comentario, 'fin')}
    </div>
  );
}
